'use client'

import { container, item } from "@/lib/framer";
import { BookOpen, Calendar, MapPin, Users } from "lucide-react";
import { motion } from "motion/react";
import { Card, CardContent } from "../ui/card";
import Image from "next/image";
import Link from "next/link";

export const Program = () => {
  return (
    <>
    <section className="bg-muted py-16">
    <div className="container mx-auto px-4">
      <motion.div
        className="mb-12 text-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        variants={container}
      >
        <motion.h2 className="text-3xl font-bold tracking-tight sm:text-4xl" variants={item}>
          Why Choose Us
        </motion.h2>
        <motion.p className="mt-4 text-muted-foreground" variants={item}>
          What makes Shree Janakalyan Boarding School special
        </motion.p>
      </motion.div>
      <motion.div
        className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={container}
      >
        {[
          {
            icon: <BookOpen className="h-6 w-6 text-primary" />,
            title: "Quality Education",
            desc: "Comprehensive curriculum designed to develop critical thinking and creativity",
          },
          {
            icon: <Users className="h-6 w-6 text-primary" />,
            title: "Experienced Faculty",
            desc: "Dedicated teachers with expertise in child development and education",
          },
          {
            icon: <Calendar className="h-6 w-6 text-primary" />,
            title: "Cultural Activities",
            desc: "Regular events celebrating Nepali traditions and cultural heritage",
          },
          {
            icon: <MapPin className="h-6 w-6 text-primary" />,
            title: "Safe Environment",
            desc: "Secure campus with modern facilities and child-friendly spaces",
          },
        ].map((feature, index) => (
          <motion.div key={index} variants={item}>
            <motion.div
              whileHover={{
                y: -10,
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Card>
                <CardContent className="flex flex-col items-center p-6 text-center">
                  <motion.div
                    className="mb-4 rounded-full bg-primary/10 p-3"
                    whileHover={{ rotate: 5, scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    {feature.icon}
                  </motion.div>
                  <h3 className="mb-2 text-xl font-bold">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>

  {/* Programs Section */}
  <section id="programs" className="py-16 md:py-24">
    <div className="container mx-auto px-4">
      <motion.div
        className="mb-12 text-center"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        variants={container}
      >
        <motion.h2 className="text-3xl font-bold tracking-tight sm:text-4xl" variants={item}>
          Our Programs
        </motion.h2>
        <motion.p className="mt-4 text-muted-foreground" variants={item}>
          Comprehensive education for children aged 3-14
        </motion.p>
      </motion.div>
      <motion.div
        className="grid gap-4 md:grid-cols-3"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={container}
      >
        {[
          {
            title: "Early Childhood (Pre Nursery - Grade 2)",
            src: "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742039213/prenmam_caznnv.jpg",
            desc: "Foundation building with focus on literacy, numeracy, and social skills through play-based learning.",
          },
          {
            title: "Middle Childhood (Grades 3-5)",
            src: "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742039212/2class_xnkvru.jpg",
            desc: "Expanding knowledge with introduction to science, social studies and extra-curricular activities.",
          },
          {
            title: "Upper Primary (Grades 6-8)",
            src: "https://res.cloudinary.com/ds1npuv1v/image/upload/v1742039211/class7_p5sula.jpg",
            desc: "Preparing for secondary education with advanced concepts, cultural education, and project-based learning.",
          },
        ].map((program, index) => (
          <motion.div key={index} variants={item}>
            <motion.div
              whileHover={{
                y: -10,
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Card>
                <div className="relative h-48 -mt-6 overflow-hidden rounded-t-lg">
                  <Image
                    src={program.src}
                    alt={program.title}
                    fill
                    className="object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="mb-2 text-xl font-bold">{program.title}</h3>
                  <p className="mb-4 text-sm text-muted-foreground">{program.desc}</p>
                  <Link href="/program" className="text-sm font-medium text-primary hover:underline">
                    Learn more →
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
  </>
  )
}